import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';

import React, { useCallback, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useFlexLayout, useRowSelect, useSortBy, useTable } from 'react-table';

import { FlexRow, Gap } from 'ui-components/components/flex';
import { Button } from 'ui-components/components/button';
import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { Text, TextType } from 'ui-components/components/text';
import FormattedCurrency from '../../../../../../components/formatted-currency';

import { getColumns } from './columns';
import { useIsUnitsOfMeasureAvailable } from '../../../../../../feature-gates/use-is-units-of-measure-available';
import { useInboundQuantities } from '../../../hooks/use-inbound-quantities';
import { computeAbsoluteEaches } from '../../../../../../constants/units-of-measure';
import { messages } from './messages';
import styles from './product-grid.styles.module.less';
import { ConfirmDialogueModal } from '../../../../../../components/modals/confirm-dialogue-modal';
import { useModal } from '../../../../../../hooks/use-modal';
import { openProductDetailModal } from '../../../../../../store/products/products.actions';
import Global from '../../../../../../store/global-selectors';
import { usePurchaseOrderContext } from '../../../purchase-order-context';

/**
 * @module ProductGrid
 * @description A component that displays a table of products added to a purchase order.
 * This component uses the PurchaseOrderContext to access and update the purchase order state.
 * It provides functionality for:
 * - Viewing products in a sortable table
 * - Removing individual products
 * - Clearing all products
 * - Updating product fields (like cost)
 * - Opening product details in a modal
 * The table is implemented using react-table with flex layout, row selection, and sorting capabilities.
 * @param {object} props - The component props
 * @param {boolean} props.isReadOnly - Whether the grid should be in read-only mode
 * @returns {JSX.Element} A container with header, product table, and footer
 * @example
 * <ProductGrid isReadOnly={false} />
 */
export const ProductGrid = ({ isReadOnly = false }) => {
  const { purchaseOrder = { products: [] }, setPurchaseOrder, isInboundEnabled } = usePurchaseOrderContext();
  const intl = useIntl();
  const { open: openClearAllModal, close: closeClearAllModal, isOpen: isClearAllModalOpen } = useModal();

  const { products } = purchaseOrder;

  const handleRemoveProduct = useCallback(
    (rowKey) => {
      setPurchaseOrder((order) => ({
        ...order,
        products: (order.products || []).filter((product) => (product.rowKey ?? `${product.productId}`) !== rowKey),
      }));
    },
    [setPurchaseOrder],
  );

  const handleClearAll = useCallback(() => {
    setPurchaseOrder((order) => ({
      ...order,
      products: [],
    }));
    closeClearAllModal();
  }, [setPurchaseOrder, closeClearAllModal]);

  const handleUpdateProduct = useCallback(
    (rowKey, field, value) => {
      setPurchaseOrder((order) => ({
        ...order,
        products: (order.products || []).map((product) => {
          if ((product.rowKey ?? `${product.productId}`) === rowKey) {
            return {
              ...product,
              [field]: value,
            };
          }
          return product;
        }),
      }));
    },
    [setPurchaseOrder],
  );

  const dispatch = useDispatch();
  const openProductDetails = useCallback(
    (productId) => {
      if (!productId) {
        return;
      }
      dispatch(openProductDetailModal(productId));
    },
    [dispatch],
  );

  const defaultCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const currencyCode = purchaseOrder.currencyCode || defaultCurrencyCode;
  const { isAvailable: isUnitsOfMeasureEnabled } = useIsUnitsOfMeasureAvailable();

  const productIds = useMemo(() => products.map((p) => p.productId), [products]);
  const { inboundsByProductId, isLoading: isInboundLoading } = useInboundQuantities(
    productIds,
    purchaseOrder.warehouseId,
    isInboundEnabled,
  );
  const isInboundLoadingRef = useRef(isInboundLoading);
  isInboundLoadingRef.current = isInboundLoading;

  const productsWithInbound = useMemo(
    () => products.map((p) => ({ ...p, inboundQuantity: inboundsByProductId[p.productId] ?? null })),
    [products, inboundsByProductId],
  );

  const orderedTotal = useMemo(() => {
    return products.reduce((sum, p) => {
      const qty = p.quantity ?? 0;
      const cost = p.cost ?? 0;
      const absoluteEaches =
        isUnitsOfMeasureEnabled && (p.unitOfMeasure ?? 0) > 0 ? computeAbsoluteEaches(p.unitOfMeasure, p.unitsOfMeasure ?? []) : 1;
      return sum + qty * cost * absoluteEaches;
    }, 0);
  }, [products, isUnitsOfMeasureEnabled]);

  const columns = useMemo(() => {
    return getColumns(
      handleUpdateProduct,
      handleRemoveProduct,
      openProductDetails,
      isReadOnly,
      currencyCode,
      isUnitsOfMeasureEnabled,
      isInboundLoadingRef,
      isInboundEnabled,
    );
  }, [handleUpdateProduct, handleRemoveProduct, openProductDetails, isReadOnly, currencyCode, isUnitsOfMeasureEnabled, isInboundEnabled]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: productsWithInbound,
    },
    useSortBy,
    useFlexLayout,
    useRowSelect,
  );

  return (
    <div className={styles['product-list-container']}>
      <div className={styles['product-list-header']}>
        <Text
          value={intl.formatMessage(messages.productsAdded, {
            count: products.length,
          })}
        />
        {!isReadOnly && (
          <Button className={styles['clear-all-button']} disabled={products.length === 0} onClick={openClearAllModal} theme='link'>
            <div className={styles['clear-text']}>
              <FontAwesomeIcon className={styles['clear-icon']} icon={faTimes} />
              <FormattedMessage {...messages.clearAll} />
            </div>
          </Button>
        )}
      </div>

      {/* memoizeRows intentionally omitted — rows must re-render when sibling UoMs change so each row's dropdown reflects which UoMs are already taken */}
      <ReactTableWrapper
        key={currencyCode}
        className={styles['product-table']}
        emptyRow={<Text className={styles['empty-row-text']} textType={TextType.Paragraph} value={messages.noProductsScanned} />}
        getTableBodyProps={getTableBodyProps}
        getTableProps={getTableProps}
        headerGroups={headerGroups}
        prepareRow={prepareRow}
        rows={rows}
      />

      <FlexRow className={styles['product-list-footer']} gap={Gap.Space16}>
        <Text
          textType={TextType.Paragraph}
          value={
            <FormattedMessage
              {...messages.showingProductSummary}
              values={{
                start: 1,
                end: products.length,
                total: products.length,
                b: (chunks) => <b>{chunks}</b>,
              }}
            />
          }
        />
        {products.length > 0 && (
          <FlexRow className={styles['ordered-total']} gap={Gap.Space8}>
            <Text
              className={styles['ordered-total-label']}
              textType={TextType.Paragraph}
              value={intl.formatMessage(messages.orderedTotal)}
            />
            <Text
              className={styles['ordered-total-value']}
              textType={TextType.Paragraph}
              value={<FormattedCurrency currencyCode={currencyCode} value={orderedTotal} />}
            />
          </FlexRow>
        )}
      </FlexRow>

      {isClearAllModalOpen ? (
        <ConfirmDialogueModal
          body={<Text textType={TextType.Paragraph} value={messages.clearAllConfirmBody} />}
          buttonMessage={<FormattedMessage {...messages.clearAllConfirmButton} />}
          header={<Text textType={TextType.H3} value={messages.clearAllConfirmHeader} />}
          onClose={closeClearAllModal}
          onConfirm={handleClearAll}
        />
      ) : null}
    </div>
  );
};
